<template>
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-6">Settings</h1>
    
    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
      <p class="mt-2 text-gray-600 dark:text-gray-400">Loading settings...</p>
    </div>

    <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline"> {{ error }}</span>
    </div>

    <div v-else class="space-y-6">
      <!-- AI Onboarding Section -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">AI-Powered Onboarding</h2>
        <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Automatically generate compliance portal content by analyzing your company website. 
          The AI will identify relevant compliances, security categories, and suggest documents and updates.
        </p>

        <!-- Onboarding Form -->
        <form v-if="!onboardingInProgress" @submit.prevent="startOnboarding(false)" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Company Website URL <span class="text-red-500">*</span>
            </label>
            <div class="flex space-x-2">
              <input
                v-model="onboardingForm.companyUrl"
                type="url"
                required
                placeholder="https://example.com"
                class="input flex-1"
                :disabled="onboardingInProgress"
              />
              <button
                type="submit"
                class="btn-primary"
                :disabled="isOnboardingLoading || !onboardingForm.companyUrl"
              >
                <span v-if="isOnboardingLoading" class="inline-block animate-spin mr-2">⟳</span>
                {{ isOnboardingLoading ? 'Starting...' : 'Start AI Analysis' }}
              </button>
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter your company's main website URL. The AI will analyze the content and generate relevant compliance portal data.
            </p>
          </div>
        </form>

        <!-- Onboarding Progress -->
        <div v-if="onboardingInProgress" class="space-y-4">
          <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-sm font-medium text-blue-900 dark:text-blue-100">AI Analysis in Progress</h3>
              <span class="text-xs text-blue-600 dark:text-blue-400">{{ currentStep }}/{{ totalSteps }}</span>
            </div>
            
            <!-- Progress Bar -->
            <div class="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2 mb-3">
              <div 
                class="bg-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                :style="{ width: `${(currentStep / totalSteps) * 100}%` }"
              ></div>
            </div>

            <!-- Current Step -->
            <div class="text-sm text-blue-700 dark:text-blue-300">
              <div class="flex items-center">
                <div class="inline-block animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent mr-2"></div>
                {{ currentStepDescription }}
              </div>
            </div>

                         <!-- Step Details -->
             <div class="mt-3 space-y-2">
               <div 
                 v-for="(step, index) in onboardingSteps" 
                 :key="index"
                 class="flex items-center text-xs transition-all duration-300"
                 :class="{
                   'text-green-600 dark:text-green-400': index < currentStep - 1,
                   'text-blue-600 dark:text-blue-400 progress-pulse': index === currentStep - 1,
                   'text-gray-400 dark:text-gray-500': index > currentStep - 1
                 }"
               >
                 <div 
                   class="w-4 h-4 rounded-full mr-2 flex items-center justify-center transition-all duration-300"
                   :class="{
                     'bg-green-500 step-complete': index < currentStep - 1,
                     'bg-blue-500 progress-pulse': index === currentStep - 1,
                     'bg-gray-300 dark:bg-gray-600': index > currentStep - 1
                   }"
                 >
                   <span v-if="index < currentStep - 1" class="text-white text-xs">✓</span>
                   <span v-else-if="index === currentStep - 1" class="text-white text-xs">⟳</span>
                   <span v-else class="text-gray-500 text-xs">{{ index + 1 }}</span>
                 </div>
                 {{ step }}
               </div>
             </div>
          </div>

          <!-- Cancel Button -->
          <button
            @click="cancelOnboarding"
            class="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            :disabled="!canCancel"
          >
            Cancel Analysis
          </button>
        </div>

        <!-- Success Message -->
        <div v-if="onboardingSuccess" class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center step-complete">
                <span class="text-white text-sm">✓</span>
              </div>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-900 dark:text-green-100">Onboarding Completed Successfully!</h3>
              <p class="text-sm text-green-700 dark:text-green-300 mt-1">
                Your compliance portal has been automatically configured with relevant content based on your company website.
              </p>
            </div>
          </div>
          
          <!-- Generated Content Summary -->
          <div class="mt-4 p-3 bg-green-100 dark:bg-green-900/30 rounded-md">
            <h4 class="text-sm font-medium text-green-800 dark:text-green-200 mb-2">Generated Content:</h4>
            <div class="grid grid-cols-2 gap-2 text-xs text-green-700 dark:text-green-300">
              <div>• Company description updated</div>
              <div>• Relevant compliances enabled</div>
              <div>• Security categories configured</div>
              <div>• Documents and updates suggested</div>
            </div>
          </div>
          
          <div class="mt-4 flex space-x-3">
            <button
              @click="resetOnboarding"
              class="text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
            >
              Start New Analysis
            </button>
            <button
              @click="viewGeneratedContent"
              class="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
            >
              View Results
            </button>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="onboardingError" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <div class="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                <span class="text-white text-sm">✕</span>
              </div>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-900 dark:text-red-100">
                {{ alreadyOnboarded ? 'Already onboarded' : 'Onboarding Failed' }}
              </h3>
              <p class="text-sm text-red-700 dark:text-red-300 mt-1">
                {{ onboardingError }}
              </p>
            </div>
          </div>
          <div class="mt-4 flex items-center gap-4">
            <button
              v-if="alreadyOnboarded"
              @click="startOnboarding(true)"
              class="text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              Re-run analysis anyway
            </button>
            <button
              @click="resetOnboarding"
              class="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
            >
              {{ alreadyOnboarded ? 'Cancel' : 'Try Again' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Single Sign-On (OIDC) -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div class="flex items-start justify-between mb-1">
          <h2 class="text-lg font-medium text-gray-900 dark:text-white">Single Sign-On (OIDC)</h2>
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            :class="form.oidc_enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'"
          >{{ form.oidc_enabled ? 'Enabled' : 'Disabled' }}</span>
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Let users in this tenant sign in via an external identity provider (Okta, Azure AD, Google Workspace, Auth0…).
          The user must still exist in this tenant's user list — SSO authenticates identity; authorisation stays here.
        </p>
        <div class="space-y-4">
          <label class="flex items-start gap-4 cursor-pointer">
            <div class="relative flex-shrink-0 mt-0.5">
              <input type="checkbox" v-model="form.oidc_enabled" class="sr-only peer" />
              <div class="w-10 h-6 rounded-full transition-colors duration-200
                bg-gray-200 dark:bg-gray-600
                peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500"></div>
              <div class="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200
                peer-checked:translate-x-4"></div>
            </div>
            <div>
              <span class="block text-sm font-medium text-gray-900 dark:text-white">Enable OIDC login</span>
              <span class="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                When enabled, a "Sign in with SSO" button appears on the login page for users in this tenant.
              </span>
            </div>
          </label>

          <div v-if="form.oidc_enabled" class="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div class="sm:col-span-2">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Issuer URL <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.oidc_issuer"
                type="url"
                placeholder="https://login.example.com"
                class="input mt-1 font-mono text-sm"
                :disabled="isSaving"
              />
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                OIDC discovery base URL. We'll fetch <code>{{ '{issuer}' }}/.well-known/openid-configuration</code>.
              </p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Client ID <span class="text-red-500">*</span>
              </label>
              <input
                v-model="form.oidc_client_id"
                type="text"
                class="input mt-1 font-mono text-sm"
                :disabled="isSaving"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Client Secret <span v-if="!oidcHasSecret" class="text-red-500">*</span>
                <span v-else class="text-xs text-gray-400 ml-1">(leave blank to keep current)</span>
              </label>
              <input
                v-model="form.oidc_client_secret"
                type="password"
                class="input mt-1 font-mono text-sm"
                :disabled="isSaving"
                :placeholder="oidcHasSecret ? '••••••••' : ''"
              />
            </div>
            <div class="sm:col-span-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <p class="text-xs text-blue-800 dark:text-blue-200 font-medium mb-1">Redirect URI to register in your IdP:</p>
              <code class="block text-xs text-blue-900 dark:text-blue-100 font-mono break-all">{{ oidcCallbackURL }}</code>
            </div>
          </div>
        </div>
      </div>

      <CustomDomainSettings />

      <!-- License Policy -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-1">Open-Source License Policy</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Flag SBOM components whose license matches any entry in this denylist.
          Use SPDX identifiers or substrings; case-insensitive. Example: <code>GPL-3.0,AGPL-3.0,SSPL-1.0</code>.
          Leave empty to disable license policy checks.
        </p>
        <textarea
          v-model="form.license_denylist"
          rows="2"
          class="input font-mono text-sm"
          placeholder="AGPL-3.0, SSPL-1.0"
          :disabled="isSaving"
        ></textarea>
        <div class="mt-4 flex items-center justify-between">
          <button
            type="button"
            class="btn-secondary btn-sm"
            :disabled="loadingViolations || !form.license_denylist"
            @click="loadLicenseViolations"
          >{{ loadingViolations ? 'Scanning…' : 'Run scan now' }}</button>
          <span v-if="licenseViolations !== null" class="text-sm text-gray-600 dark:text-gray-400">
            {{ licenseViolations.length }} violation{{ licenseViolations.length === 1 ? '' : 's' }}
          </span>
        </div>
        <!-- overflow-x-auto, not overflow-hidden: this table has four columns
             and on a phone the last one was clipped out of reach entirely. -->
        <div v-if="licenseViolations && licenseViolations.length" class="mt-4 border border-gray-200 dark:border-gray-700 rounded-md overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 dark:bg-gray-900/40 text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th class="px-3 py-2 text-left">Component</th>
                <th class="px-3 py-2 text-left">Version</th>
                <th class="px-3 py-2 text-left">License</th>
                <th class="px-3 py-2 text-left">Rule matched</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
              <tr v-for="(v, i) in licenseViolations" :key="i" class="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td class="px-3 py-2 font-mono text-xs">{{ v.name }}</td>
                <td class="px-3 py-2 text-xs">{{ v.version }}</td>
                <td class="px-3 py-2 text-xs">{{ v.license }}</td>
                <td class="px-3 py-2 text-xs font-medium text-red-600 dark:text-red-400">{{ v.rule }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else-if="licenseViolations && !licenseViolations.length" class="mt-3 text-sm text-green-600 dark:text-green-400">
          No components violate the current policy.
        </p>
      </div>

      <!-- Vulnerability Ignore Lists -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-1">Vulnerability Ignore Lists</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Tenant-wide blocklists applied to SBOM-finding webhooks and the portfolio view.
          Findings still appear in the database (so audits remain complete) but are flagged
          <code>ignored</code> and skipped by alerts. Per-webhook lists layer additively on top.
        </p>
        <div class="grid md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ignore CVEs <span class="text-xs text-gray-400 ml-1">(comma-separated; exact match)</span>
            </label>
            <textarea
              v-model="form.ignore_cves"
              rows="3"
              class="input font-mono text-sm w-full"
              placeholder="CVE-2024-12345, EUVD-2025-0001"
              :disabled="isSaving"
            ></textarea>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ignore PURLs <span class="text-xs text-gray-400 ml-1">(substring match)</span>
            </label>
            <textarea
              v-model="form.ignore_purls"
              rows="3"
              class="input font-mono text-sm w-full"
              placeholder="pkg:npm/lodash@, pkg:maven/com.example/legacy"
              :disabled="isSaving"
            ></textarea>
          </div>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Tip: prefer ignoring a PURL prefix (e.g. <code>pkg:npm/lodash@4.17.</code>) over individual CVEs
          when accepting risk on a known-legacy component.
        </p>
      </div>

      <!-- Notification Settings -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-1">Notification Settings</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Control what triggers email notifications to your verified security update subscribers.
          All notifications are <strong class="text-gray-700 dark:text-gray-300">disabled by default</strong> — enable only what you want subscribers to receive.
        </p>
        <div class="space-y-4">
          <label class="flex items-start gap-4 cursor-pointer group">
            <div class="relative flex-shrink-0 mt-0.5">
              <input type="checkbox" v-model="form.notify_on_vulns" class="sr-only peer" />
              <div class="w-10 h-6 rounded-full transition-colors duration-200
                bg-gray-200 dark:bg-gray-600
                peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500
                peer-focus:ring-2 peer-focus:ring-blue-500 peer-focus:ring-offset-2 dark:peer-focus:ring-offset-gray-800">
              </div>
              <div class="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200
                peer-checked:translate-x-4">
              </div>
            </div>
            <div>
              <span class="block text-sm font-medium text-gray-900 dark:text-white">Notify on vulnerability disclosures</span>
              <span class="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Send an email when a vulnerability is made public. Only fires for
                <strong class="text-gray-600 dark:text-gray-300">public</strong> vulnerabilities — private ones are never included.
              </span>
            </div>
          </label>

          <label class="flex items-start gap-4 cursor-pointer group">
            <div class="relative flex-shrink-0 mt-0.5">
              <input type="checkbox" v-model="form.notify_on_updates" class="sr-only peer" />
              <div class="w-10 h-6 rounded-full transition-colors duration-200
                bg-gray-200 dark:bg-gray-600
                peer-checked:bg-blue-600 dark:peer-checked:bg-blue-500
                peer-focus:ring-2 peer-focus:ring-blue-500 peer-focus:ring-offset-2 dark:peer-focus:ring-offset-gray-800">
              </div>
              <div class="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200
                peer-checked:translate-x-4">
              </div>
            </div>
            <div>
              <span class="block text-sm font-medium text-gray-900 dark:text-white">Notify on security updates &amp; new product versions</span>
              <span class="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Send an email when you publish a new security update or release a new product version.
              </span>
            </div>
          </label>
        </div>
        <p class="mt-4 text-xs text-gray-400 dark:text-gray-500">
          Every notification email contains a one-click unsubscribe link. Subscribers manage their own preferences.
        </p>
      </div>

      <!-- Basic Information -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Basic Information</h2>
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Friendly Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.friendly_name"
              type="text"
              required
              class="input mt-1"
              :disabled="isSaving"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Domain Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.domain_name"
              type="text"
              required
              class="input mt-1"
              :disabled="isSaving"
            />
          </div>
          <div class="sm:col-span-2">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea
              v-model="form.description"
              rows="3"
              class="input mt-1"
              :disabled="isSaving"
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Contact Information -->
      <div class="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Contact Information</h2>
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Website</label>
            <input
              v-model="form.website"
              type="url"
              class="input mt-1"
              :disabled="isSaving"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Owner Name</label>
            <input
              v-model="form.owner"
              type="text"
              class="input mt-1"
              :disabled="isSaving"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Owner Email</label>
            <input
              v-model="form.owner_email"
              type="email"
              class="input mt-1"
              :disabled="isSaving"
            />
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <button
          @click="saveSettings"
          class="btn-primary"
          :disabled="isSaving"
        >
          <span v-if="isSaving" class="inline-block animate-spin mr-2">⟳</span>
          {{ isSaving ? 'Saving...' : 'Save Changes' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import { ErrorTypes, ErrorMessages } from '@/utils/errorHandler'
import CustomDomainSettings from '@/components/CustomDomainSettings.vue'

const getApiBase = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const clean = envUrl.replace(/\/$/, '')
  return clean.endsWith('/api/v1') ? clean : `${clean}/api/v1`
}

export default {
  name: 'SettingsView',
  components: { CustomDomainSettings },
  setup() {
    const store = useStore()
    const isSaving = ref(false)
    const loading = ref(true)
    const error = ref(null)

    // Onboarding progress is persisted per tenant. Unscoped keys meant an
    // admin who started a run for one tenant then switched to another saw the
    // first tenant's progress bar — and could resume polling a status ID that
    // does not belong to the tenant now on screen.
    const onboardingKey = (name) => `onboarding_${store.state.currentTenantId}_${name}`
    const readOnboarding = (name) => localStorage.getItem(onboardingKey(name))

    // Onboarding state
    const onboardingInProgress = ref(JSON.parse(readOnboarding('InProgress') || 'false'))
    const onboardingSuccess = ref(JSON.parse(readOnboarding('Success') || 'false'))
    const onboardingError = ref(readOnboarding('Error') || null)
    // Set when the backend rejects a run because this tenant is already
    // onboarded, which is recoverable via an explicit re-run rather than a
    // dead end. Deliberately not persisted: it describes the last attempt.
    const alreadyOnboarded = ref(false)
    const currentStep = ref(parseInt(readOnboarding('CurrentStep') || '1'))
    const totalSteps = ref(7)
    const canCancel = ref(JSON.parse(readOnboarding('CanCancel') || 'false'))
    const isOnboardingLoading = ref(false)

    const onboardingForm = ref({
      companyUrl: readOnboarding('CompanyUrl') || ''
    })

    const onboardingSteps = [
      'Analyzing company website',
      'Identifying relevant compliances',
      'Processing security categories',
      'Detecting security content',
      'Creating security documents',
      'Creating security updates',
      'Finalizing configuration'
    ]

    const currentStepDescription = ref(readOnboarding('CurrentStepDescription') || onboardingSteps[0])

    // Helper functions to save/restore onboarding state
    const saveOnboardingState = () => {
      localStorage.setItem(onboardingKey('InProgress'), onboardingInProgress.value.toString())
      localStorage.setItem(onboardingKey('Success'), onboardingSuccess.value.toString())
      localStorage.setItem(onboardingKey('Error'), onboardingError.value || '')
      localStorage.setItem(onboardingKey('CurrentStep'), currentStep.value.toString())
      localStorage.setItem(onboardingKey('CanCancel'), canCancel.value.toString())
      localStorage.setItem(onboardingKey('CompanyUrl'), onboardingForm.value.companyUrl)
      localStorage.setItem(onboardingKey('CurrentStepDescription'), currentStepDescription.value)
    }

    const clearOnboardingState = () => {
      for (const name of ['InProgress', 'Success', 'Error', 'CurrentStep',
        'CanCancel', 'CompanyUrl', 'CurrentStepDescription', 'StatusId']) {
        localStorage.removeItem(onboardingKey(name))
      }
    }

    const checkExistingOnboarding = async () => {
      // If onboarding was in progress, try to resume polling
      if (onboardingInProgress.value && !onboardingSuccess.value) {
        const statusId = readOnboarding('StatusId')
        if (statusId && store.state.currentTenantId) {
          try {
            // Try to get current status
            const status = await store.dispatch('getOnboardingStatus', {
              tenantId: store.state.currentTenantId,
              statusId: statusId
            })
            
            // If still in progress, resume polling
            if (['pending', 'processing'].includes(status.status)) {
              await pollOnboardingStatus(statusId)
            } else {
              // Process completed or failed
              if (status.status === 'completed') {
                onboardingSuccess.value = true
                currentStep.value = totalSteps.value
                currentStepDescription.value = 'Configuration completed successfully'
                onboardingInProgress.value = false
                canCancel.value = false
              } else if (status.status === 'failed') {
                onboardingError.value = status.error_message || 'Onboarding failed'
                onboardingInProgress.value = false
                canCancel.value = false
              }
              clearOnboardingState()
            }
          } catch (error) {
            // If we can't get status, assume it's done and clear state
            clearOnboardingState()
            onboardingInProgress.value = false
            onboardingError.value = 'Unable to retrieve onboarding status'
          }
        }
      }
    }

    const form = ref({
      friendly_name: '',
      domain_name: '',
      description: '',
      website: '',
      owner: '',
      owner_email: '',
      notify_on_vulns: false,
      notify_on_updates: false,
      license_denylist: '',
      ignore_cves: '',
      ignore_purls: '',
      oidc_enabled: false,
      oidc_issuer: '',
      oidc_client_id: '',
      oidc_client_secret: '',
    })

    // Tracks whether the tenant already has a stored OIDC secret so the UI
    // can show "leave blank to keep" hint instead of making the admin retype
    // the secret every time they touch another OIDC field.
    const oidcHasSecret = ref(false)

    const oidcCallbackURL = computed(() => {
      const tenantId = store.state.currentTenantId || '<tenant_id>'
      const base = getApiBase().replace(/\/api\/v1$/, '')
      return `${base}/api/v1/auth/oidc/${tenantId}/callback`
    })

    const licenseViolations = ref(null)
    const loadingViolations = ref(false)
    const loadLicenseViolations = async () => {
      const tenantId = store.state.currentTenantId
      if (!tenantId) return
      try {
        loadingViolations.value = true
        // Save any pending denylist edit before scanning so results reflect
        // the current textarea value rather than the previously persisted list.
        await store.dispatch('updateTenantData', { tenantId, data: { license_denylist: form.value.license_denylist } })
        const resp = await axios.get(
          `${getApiBase()}/admin/tenants/${tenantId}/license-violations`,
          { headers: { Authorization: `Bearer ${store.state.token}` } }
        )
        licenseViolations.value = resp.data?.violations || []
      } catch (err) {
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: err.response?.data?.error || 'Failed to run license scan',
          duration: 5000
        })
      } finally {
        loadingViolations.value = false
      }
    }

    const loadTenantData = async () => {
      loading.value = true
      error.value = null
      try {
        const tenantId = store.state.currentTenantId
        if (!tenantId) {
          throw new Error(ErrorMessages[ErrorTypes.VALIDATION].tenant || 'No tenant selected')
        }

        const tenantData = await store.dispatch('getTenantData', tenantId)
        if (!tenantData) {
          throw new Error(ErrorMessages[ErrorTypes.VALIDATION].load || 'Failed to load tenant data')
        }

        form.value = {
          friendly_name: tenantData.friendly_name || '',
          domain_name: tenantData.domain_name || '',
          description: tenantData.description || '',
          website: tenantData.website || '',
          owner: tenantData.owner || '',
          owner_email: tenantData.owner_email || '',
          notify_on_vulns: tenantData.notify_on_vulns ?? false,
          notify_on_updates: tenantData.notify_on_updates ?? false,
          license_denylist: tenantData.license_denylist || '',
          ignore_cves: tenantData.ignore_cves || '',
          ignore_purls: tenantData.ignore_purls || '',
          oidc_enabled: tenantData.oidc_enabled ?? false,
          oidc_issuer: tenantData.oidc_issuer || '',
          oidc_client_id: tenantData.oidc_client_id || '',
          oidc_client_secret: '',
        }
        // The API never returns the secret; infer "already set" from the
        // oidc_enabled flag + presence of issuer/client_id. Good enough as a UX hint.
        oidcHasSecret.value = !!(tenantData.oidc_enabled || tenantData.oidc_client_id)
      } catch (err) {
        const errorType = err.response?.status === 403 ? ErrorTypes.AUTHORIZATION : 
                         err.response?.status === 400 ? ErrorTypes.VALIDATION : 
                         ErrorTypes.UNKNOWN
        
        error.value = ErrorMessages[errorType].load || err.message || 'Failed to load tenant data'
        
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: error.value,
          duration: 5000
        })
      } finally {
        loading.value = false
      }
    }

    const saveSettings = async () => {
      if (!store.state.currentTenantId) {
        error.value = ErrorMessages[ErrorTypes.VALIDATION].tenant || 'No tenant selected'
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: error.value,
          duration: 5000
        })
        return
      }

      try {
        isSaving.value = true
        error.value = null

        // Validate required fields
        if (!form.value.friendly_name || !form.value.domain_name) {
          throw new Error(ErrorMessages[ErrorTypes.VALIDATION].required || 'Friendly name and domain name are required')
        }

        // Validate email format if provided
        if (form.value.owner_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.value.owner_email)) {
          throw new Error(ErrorMessages[ErrorTypes.VALIDATION].email || 'Invalid email format')
        }

        // Only send the OIDC secret if the admin typed one; otherwise the
        // backend preserves the stored value (empty string is treated as "no change").
        const payload = { ...form.value }
        if (!payload.oidc_client_secret) {
          delete payload.oidc_client_secret
        }

        await store.dispatch('updateTenantData', {
          tenantId: store.state.currentTenantId,
          data: payload
        })

        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'Settings saved successfully',
          duration: 3000
        })
      } catch (err) {
        const errorType = err.response?.status === 403 ? ErrorTypes.AUTHORIZATION : 
                         err.response?.status === 400 ? ErrorTypes.VALIDATION : 
                         ErrorTypes.UNKNOWN
        
        error.value = ErrorMessages[errorType].save || err.message || 'Failed to save settings'
        
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: error.value,
          duration: 5000
        })
      } finally {
        isSaving.value = false
      }
    }

    // Onboarding functions
    const startOnboarding = async (forceRerun = false) => {
      if (!store.state.currentTenantId) {
        onboardingError.value = 'No tenant selected'
        return
      }

      if (!onboardingForm.value.companyUrl) {
        onboardingError.value = 'Please enter a company website URL'
        return
      }

      try {
        onboardingInProgress.value = true
        isOnboardingLoading.value = true
        onboardingSuccess.value = false
        onboardingError.value = null
        alreadyOnboarded.value = false
        currentStep.value = 1
        currentStepDescription.value = 'Starting AI analysis...'
        canCancel.value = true

        // Start the onboarding process
        const response = await store.dispatch('startOnboarding', {
          tenantId: store.state.currentTenantId,
          companyUrl: onboardingForm.value.companyUrl,
          forceRerun
        })

        if (response.success && response.status_id) {
          // Save status ID for persistence
          localStorage.setItem(onboardingKey('StatusId'), response.status_id)
          saveOnboardingState()
          
          // Poll for status updates
          await pollOnboardingStatus(response.status_id)
        } else {
          throw new Error(response.message || 'Onboarding failed to start')
        }
              } catch (err) {
        // status/code are attached by the startOnboarding store action; the
        // raw axios error object is not rethrown, so err.response is undefined.
        if (err.status === 409) {
          onboardingError.value = 'Onboarding already in progress for this tenant. Please wait for the current process to complete.'
        } else if (err.status === 403 && err.code === 'ALREADY_ONBOARDED') {
          alreadyOnboarded.value = true
          onboardingError.value = 'This tenant has already been onboarded. Re-running will overwrite the generated content with a fresh analysis.'
        } else {
          // Provide more user-friendly error messages for common issues
          let errorMessage = err.message || 'An error occurred during onboarding'
          
          if (errorMessage.includes('403') || errorMessage.includes('access denied') || errorMessage.includes('forbidden')) {
            errorMessage = 'The company website is not accessible. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
          } else if (errorMessage.includes('timeout')) {
            errorMessage = 'The website analysis timed out. This could be due to a slow website or network issues. Please try again or use a different website.'
          } else if (errorMessage.includes('connection') || errorMessage.includes('network')) {
            errorMessage = 'Unable to connect to the website. Please check the URL and ensure the website is accessible.'
          } else if (errorMessage.includes('invalid URL') || errorMessage.includes('malformed')) {
            errorMessage = 'The provided URL is invalid. Please check the format and try again.'
          } else if (errorMessage.includes('Failed to fetch website')) {
            errorMessage = 'Unable to access the company website. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
          }
          
          onboardingError.value = errorMessage
        }
        
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: onboardingError.value,
          duration: 8000
        })
        
        // Reset state on error
        onboardingInProgress.value = false
        isOnboardingLoading.value = false
        canCancel.value = false
        clearOnboardingState()
      }
    }

    const pollOnboardingStatus = async (statusId) => {
      const maxAttempts = 3600 // 1 hour with 1-second intervals
      let attempts = 0
      let consecutiveErrors = 0
      const maxConsecutiveErrors = 5 // Stop polling after 5 consecutive errors

      const pollInterval = setInterval(async () => {
        attempts++
        
        try {
          const status = await store.dispatch('getOnboardingStatus', {
            tenantId: store.state.currentTenantId,
            statusId: statusId
          })

          // Reset consecutive error count on success
          consecutiveErrors = 0

          // Update progress
          currentStep.value = Math.ceil((status.progress / 100) * totalSteps.value)
          currentStepDescription.value = status.current_step
          saveOnboardingState()

          if (status.status === 'completed') {
            clearInterval(pollInterval)
            onboardingSuccess.value = true
            currentStep.value = totalSteps.value
            currentStepDescription.value = 'Configuration completed successfully'
            
            // Reset state on completion
            onboardingInProgress.value = false
            isOnboardingLoading.value = false
            canCancel.value = false
            clearOnboardingState()
            
            // Refresh tenant data to show updated friendly name and description
            await loadTenantData()
            
            // Clear any frontend cache and refresh all tenant-related data
            try {
              // Refresh all tenant data to ensure we have the latest information
              await store.dispatch('fetchTenants')
              
              // Clear any cached data for the current tenant
              const tenantId = store.state.currentTenantId
              if (tenantId) {
                // Refresh all data modules for the tenant
                await Promise.all([
                  store.dispatch('securityCategories/fetchData', { tenantId, page: 1, entriesPerPage: 10, search: '', orderBy: 'id', sortDirection: 'asc' }),
                  store.dispatch('compliances/fetchData', { tenantId, page: 1, entriesPerPage: 10, search: '', orderBy: 'id', sortDirection: 'asc' }),
                  store.dispatch('documents/fetchData', { tenantId, page: 1, entriesPerPage: 10, search: '', orderBy: 'id', sortDirection: 'asc' }),
                  store.dispatch('updates/fetchData', { tenantId, page: 1, entriesPerPage: 10, search: '', orderBy: 'id', sortDirection: 'asc' }),
                  store.dispatch('vulnerabilities/fetchData', { tenantId, page: 1, entriesPerPage: 10, search: '', orderBy: 'id', sortDirection: 'asc' }),
                  store.dispatch('users/fetchData', { tenantId, page: 1, entriesPerPage: 10, search: '', orderBy: 'id', sortDirection: 'asc' })
                ])
              }
            } catch (refreshError) {
              console.warn('Failed to refresh some tenant data after onboarding:', refreshError)
              // Don't fail the onboarding success for refresh errors
            }
            
            store.dispatch('notification/showNotification', {
              type: 'success',
              message: 'AI onboarding completed successfully! Your compliance portal has been configured.',
              duration: 5000
            })
          } else if (status.status === 'failed') {
            clearInterval(pollInterval)
            
            // Handle specific error cases with better user messages
            let errorMessage = status.error_message || 'Onboarding failed'
            
            // Provide more user-friendly error messages
            if (errorMessage.includes('403') || errorMessage.includes('access denied') || errorMessage.includes('forbidden')) {
              errorMessage = 'The company website is not accessible. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
            } else if (errorMessage.includes('timeout')) {
              errorMessage = 'The website analysis timed out. This could be due to a slow website or network issues. Please try again or use a different website.'
            } else if (errorMessage.includes('connection') || errorMessage.includes('network')) {
              errorMessage = 'Unable to connect to the website. Please check the URL and ensure the website is accessible.'
            } else if (errorMessage.includes('invalid URL') || errorMessage.includes('malformed')) {
              errorMessage = 'The provided URL is invalid. Please check the format and try again.'
            }
            
            onboardingError.value = errorMessage
            onboardingInProgress.value = false
            isOnboardingLoading.value = false
            canCancel.value = false
            clearOnboardingState()
            
            store.dispatch('notification/showNotification', {
              type: 'error',
              message: errorMessage,
              duration: 8000
            })
          }

          // Stop polling if we've exceeded max attempts
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval)
            onboardingError.value = 'Onboarding timed out after 1 hour. Please try again or contact support.'
            onboardingInProgress.value = false
            isOnboardingLoading.value = false
            canCancel.value = false
            clearOnboardingState()
            
            store.dispatch('notification/showNotification', {
              type: 'error',
              message: 'Onboarding timed out after 1 hour. Please try again or contact support.',
              duration: 8000
            })
          }
        } catch (error) {
          consecutiveErrors++
          
          // If we've had too many consecutive errors, stop polling
          if (consecutiveErrors >= maxConsecutiveErrors) {
            clearInterval(pollInterval)
            
            let errorMessage = 'Unable to check onboarding status due to repeated network errors. Please refresh the page and try again.'
            
            // Reset state on error
            onboardingInProgress.value = false
            isOnboardingLoading.value = false
            canCancel.value = false
            onboardingError.value = errorMessage
            clearOnboardingState()
            
            store.dispatch('notification/showNotification', {
              type: 'error',
              message: errorMessage,
              duration: 8000
            })
            return
          }
          
          // Handle specific error cases with better user messages
          let errorMessage = error.message || 'An error occurred during onboarding'
          
          // Provide more user-friendly error messages for common issues
          if (errorMessage.includes('403') || errorMessage.includes('access denied') || errorMessage.includes('forbidden')) {
            errorMessage = 'The company website is not accessible. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
          } else if (errorMessage.includes('timeout')) {
            errorMessage = 'The website analysis timed out. This could be due to a slow website or network issues. Please try again or use a different website.'
          } else if (errorMessage.includes('connection') || errorMessage.includes('network')) {
            errorMessage = 'Unable to connect to the website. Please check the URL and ensure the website is accessible.'
          } else if (errorMessage.includes('invalid URL') || errorMessage.includes('malformed')) {
            errorMessage = 'The provided URL is invalid. Please check the format and try again.'
          } else if (errorMessage.includes('Failed to fetch website')) {
            errorMessage = 'Unable to access the company website. This could be due to access restrictions, authentication requirements, or the website blocking automated access. Please try with a different website or contact support.'
          }
          
          // For network errors, just log and continue polling (up to max consecutive errors)
          if (consecutiveErrors < maxConsecutiveErrors) {
            console.warn(`Onboarding status check failed (attempt ${consecutiveErrors}/${maxConsecutiveErrors}):`, errorMessage)
            return // Continue polling
          }
          
          // Reset state on error
          onboardingInProgress.value = false
          isOnboardingLoading.value = false
          canCancel.value = false
          onboardingError.value = errorMessage
          clearOnboardingState()
          
          store.dispatch('notification/showNotification', {
            type: 'error',
            message: errorMessage,
            duration: 8000
          })
        }
      }, 1000) // Poll every second
    }

    const cancelOnboarding = () => {
      if (canCancel.value) {
        onboardingInProgress.value = false
        onboardingError.value = 'Onboarding was cancelled by user'
        canCancel.value = false
        clearOnboardingState()
      }
    }

    const resetOnboarding = () => {
      onboardingInProgress.value = false
      onboardingSuccess.value = false
      onboardingError.value = null
      alreadyOnboarded.value = false
      currentStep.value = 1
      currentStepDescription.value = onboardingSteps[0]
      onboardingForm.value.companyUrl = ''
      clearOnboardingState()
    }

    const viewGeneratedContent = () => {
      // Navigate to the main sections to see the generated content
      const tenantId = store.state.currentTenantId
      if (tenantId) {
        // You can navigate to different sections or show a modal with results
        store.dispatch('notification/showNotification', {
          type: 'info',
          message: 'Navigate to Compliances, Security Categories, Documents, or Updates to see the generated content.',
          duration: 5000
        })
      }
    }

    onMounted(async () => {
      await loadTenantData()
      await checkExistingOnboarding()
    })

    return {
      form,
      isSaving,
      loading,
      error,
      saveSettings,
      // Onboarding
      onboardingForm,
      onboardingInProgress,
      onboardingSuccess,
      onboardingError,
      alreadyOnboarded,
      currentStep,
      totalSteps,
      currentStepDescription,
      onboardingSteps,
      canCancel,
      isOnboardingLoading,
      startOnboarding,
      cancelOnboarding,
      resetOnboarding,
      viewGeneratedContent,
      // OIDC + license policy
      oidcHasSecret,
      oidcCallbackURL,
      licenseViolations,
      loadingViolations,
      loadLicenseViolations
    }
  }
}
</script> 